import { FieldErrors } from 'react-hook-form'
import { isNaN } from 'lodash/fp'
import get from 'lodash/get'
import { $enum } from 'ts-enum-util'
import { Maybe } from 'types/graphqlTypes'
import * as yup from 'yup'
import { ISourceChannel, ISourceSegment } from '../types'
import { CarrierApplicationSchema } from './CarrierApplicationSchema'

const PHONE_REQUIRED_MESSAGE = 'Contact Phone number required'
const RATE_REQUIRED_MESSAGE = 'Rate is required'
const SOURCE_CHANNEL_REQUIRED_MESSAGE = 'Source channel required'
const SOURCE_SEGMENT_REQUIRED_MESSAGE = 'Source segment required'

export const SOURCE_CHANNEL_VALUES = $enum(ISourceChannel).getValues()
export const SOURCE_SEGMENT_VALUES = $enum(ISourceSegment).getValues()

function validateCounterAmount(this: yup.TestContext, value: Maybe<number>): boolean {
  if (!value) {
    return true
  }

  const {
    parent: { amount },
  } = this

  return value < amount
}

export const CarrierOptionSchema = yup.object({
  carrierApplication: CarrierApplicationSchema.nullable(),
  carrierContactName: yup.string(),
  carrierContactPhone: yup.string().required(PHONE_REQUIRED_MESSAGE),
  carrierContactPhoneExt: yup.string(),
  carrierContactUserId: yup.string().notRequired(),
  amount: yup
    .number()
    .nullable()
    .transform(val => (isNaN(val) ? null : val))
    .required(RATE_REQUIRED_MESSAGE),
  note: yup.string(),
  sourceChannel: yup.mixed<ISourceChannel | null>().when('$isSourceFieldsRequired', {
    is: true,
    then: yup
      .mixed<ISourceChannel | null>()
      .oneOf(SOURCE_CHANNEL_VALUES, SOURCE_CHANNEL_REQUIRED_MESSAGE)
      .required(SOURCE_CHANNEL_REQUIRED_MESSAGE),
    otherwise: yup.mixed<ISourceChannel | null>(),
  }),
  sourceSegment: yup.mixed<ISourceSegment | null>().when('$isSourceFieldsRequired', {
    is: true,
    then: yup
      .mixed<ISourceSegment | null>()
      .oneOf(SOURCE_SEGMENT_VALUES, SOURCE_SEGMENT_REQUIRED_MESSAGE)
      .required(SOURCE_SEGMENT_REQUIRED_MESSAGE),
    otherwise: yup.mixed<ISourceSegment | null>(),
  }),
  nonBookable: yup.boolean().default(false),
  counterOfferAmount: yup
    .number()
    .positive()
    .nullable()
    .notRequired()
    .test(
      'invalid-counter-amount',
      'Counter offer must be lower than the original rate',
      validateCounterAmount
    ),
  getApproval: yup.boolean().default(false),
})

export type TCarrierOptionSchema = yup.InferType<typeof CarrierOptionSchema>

export const getErrorMessage = <T extends FieldErrors<TCarrierOptionSchema>, K extends keyof T>(
  errors: T,
  keyName: K
): string | undefined => {
  return get(errors, [keyName, 'message'])
}
